import React, { HTMLProps, MutableRefObject, Ref, RefObject } from 'react';
import type { FactoryOpts } from 'imask';
import { FieldErrors, DefaultValues, UseFormReturn } from 'react-hook-form';

export const enum FieldType {
  Text = 'text',
  LongText = 'longtext',
  Textarea = 'textarea', // legacy case
  TextMasked = 'text-masked',
  Number = 'number',
  Email = 'email',
  Password = 'password',
  Select = 'select',
  SelectCreatable = 'select-creatable',
  Autocomplete = 'autocomplete',
  ServerAutocomplete = 'server-autocomplete',
  Date = 'date',
  File = 'file',
  Checkbox = 'checkbox',
  RadioGroup = 'radio-group',
  Boolean = 'boolean',
  Group = 'group',
  Color = 'color',
  Editor = 'editor',
  Duration = 'duration',
  Time = 'time',
  References = 'references',
  Score = 'score',
}

export type FormRegistry = {
  [FieldType.Text]: React.ComponentType<FieldArgs<TextField>>;
  [FieldType.LongText]: React.ComponentType<FieldArgs<TextField>>;
  [FieldType.Textarea]: React.ComponentType<FieldArgs<TextField>>;
  [FieldType.TextMasked]: React.ComponentType<FieldArgs<MaskedTextField>>;
  [FieldType.Number]: React.ComponentType<FieldArgs<NumberField>>;
  [FieldType.Email]: React.ComponentType<FieldArgs<EmailField>>;
  [FieldType.Password]: React.ComponentType<FieldArgs<PasswordField>>;
  [FieldType.Select]: React.ComponentType<FieldArgs<SelectField>>;
  [FieldType.Date]: React.ComponentType<FieldArgs<DateField>>;
  [FieldType.SelectCreatable]: React.ComponentType<FieldArgs<SelectField>>;
  [FieldType.Autocomplete]: React.ComponentType<FieldArgs<SelectField>>;
  [FieldType.File]: React.ComponentType<FieldArgs<FileField>>;
  [FieldType.ServerAutocomplete]: React.ComponentType<
    FieldArgs<ServerAutocompleteField>
  >;
  [FieldType.Checkbox]: React.ComponentType<FieldArgs<CheckboxField>>;
  [FieldType.RadioGroup]: React.ComponentType<FieldArgs<RadioGroupField>>;
  [FieldType.Boolean]: React.ComponentType<FieldArgs<CheckboxField>>;
  [FieldType.Group]: React.ComponentType<GroupField>;
  [FieldType.Color]: React.ComponentType<ColorField>;
  [FieldType.Editor]: React.ComponentType<EditorField>;
  [FieldType.Duration]: React.ComponentType<DurationField>;
  [FieldType.Time]: React.ComponentType<FieldArgs<TimeField>>;
  [FieldType.References]: React.ComponentType<ReferencesField>;
  [FieldType.Score]: React.ComponentType<FieldArgs<ScoreField>>;
};

export type AdapterProps = {
  [Key in FieldType]: React.ComponentProps<FormRegistry[Key]>;
};

export type Field = {
  name: string;
  label?: React.ReactNode;
  type: FieldType;
  rules?: Record<string, any>;
  disabled?: boolean;
  autoFocus?: boolean;
  autoComplete?: string;
  ref?: any;
};

export type TextField = Field & {
  type: FieldType.Text;
  multiline?: boolean;
  minRows?: number;
  placeholder?: string;
  size?: string;
};

export type NumberField = Field & {
  type: FieldType.Number;
  max: number;
  size?: string;
  min: number;
  step?: string;
};

export type EmailField = Field & {
  type: FieldType.Email;
};

export type PasswordField = Field & {
  type: FieldType.Password;
  autoComplete?: HTMLProps<HTMLInputElement>['autoComplete'];
};

export type DateField = Field & {
  type: FieldType.Date;
};

export type FileField = Field & {
  type: FieldType.File;
  multiple?: boolean;
  uploadLabel?: string;
  accept?: string;
};

type Option = {
  value: string | number;
  label: string;
};

export type SelectField = Field & {
  type: FieldType.Select;
  options: Option[];
  allowCustomChoice?: boolean;
};

export type ServerAutocompleteField = Field & {
  type: FieldType.ServerAutocomplete;
  optionsUrl: string;
  multiple?: boolean;
  params?: Record<string, any>;
  /** When true, search is sent as params={"query":"..."} instead of ?query=... */
  jsonParams?: boolean;
};

export type SelectCreatableField = Field & {
  type: FieldType.SelectCreatable;
  multiple?: boolean;
  options: Option[];
};

export type AutocompleteField = Field & {
  type: FieldType.Autocomplete;
  options: Option[];
  multiple?: boolean;
};

export type RadioGroupField = Field & {
  type: FieldType.RadioGroup;
  disabled?: boolean;
  options: Option[];
  defaultValue?: boolean;
};

export type MaskedTextField = Field & {
  type: FieldType.TextMasked;
  disabled?: boolean;
  mask?: string;
  imaskOptions?: FactoryOpts;
  placeholderChar?: string;
  normalize?: 'number' | 'decimal' | 'none';
  defaultValue?: string | number;
};

export type TimeField = Field & {
  type: FieldType.Time;
  placeholderChar?: string;
  defaultValue?: string;
};

export type CheckboxField = Field & {
  type: FieldType.Checkbox;
  defaultChecked?: boolean;
  disabled?: boolean;
};

export type GroupField = {
  type: FieldType.Group;
  label: string;
  items: FormField[];
};

export type ColorField = Field & {
  type: FieldType.Color;
  label: string;
};

export type EditorField = Field & {};

export type DurationField = Field & {};

export type ReferencesField = Field & {
  references: {
    name: string;
    url?: string;
    label?: string;
  }[];
};

export type ScoreField = Field & {
  type: FieldType.Score;
  defaultValue?: number;
};

export type FormField =
  | TextField
  | NumberField
  | EmailField
  | PasswordField
  | SelectField
  | SelectCreatableField
  | AutocompleteField
  | ServerAutocompleteField
  | FileField
  | DateField
  | ColorField
  | EditorField
  | CheckboxField
  | RadioGroupField
  | DurationField
  | MaskedTextField
  | TimeField
  | ReferencesField
  | ScoreField;

export type FieldArgs<Field extends FormField> = Omit<Field, 'type'>;

export type FormFields<Values extends object> = {
  [K in keyof Values]: FormField;
};

type AsyncDefaultValues<TFieldValues> = (
  payload?: unknown,
) => Promise<TFieldValues>;

export type FormMethods<Values extends Record<string, any>> =
  UseFormReturn<Values>;

export type FormProps<Values extends object> = {
  onSubmit?(values: Values, event: any): void | Promise<unknown>;
  onChange?(values: Values): void;
  onBeforeSubmit?(): void;
  onCancel?(): void;
  defaultValues?: DefaultValues<Values> | AsyncDefaultValues<Values>;
  errors?: FieldErrors<Values>;
  fields?: Array<FormField & { name: keyof Values | string }>;
  slots?: {
    save?: string;
    cancel?: string;
  };
  renderButtons?: boolean;
  buttonAlign?: 'left' | 'center' | 'right';
  /** When set, the native `<form>` gets this `id` so external controls can use `form="…"` + `type="submit"`. */
  formId?: string;
  formRef?: MutableRefObject<FormMethods<Values>> | any;
  extraActions?: React.ReactNode;
};
